import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { format } from 'date-fns';
import { X, RotateCcw, ArrowLeftRight, History, Clock, User, Bot, ChevronDown, ChevronRight } from 'lucide-react';
import { supabase } from '../lib/supabase';
import {
  getVersions,
  getSessionVersionsWithAiBaseline,
  AI_BASELINE_VERSION_NUMBER,
} from '../lib/assessment-versions';
import type { AssessmentVersion, AssessmentType } from '../lib/assessment-versions';
import { getSessionVersionDiffs, valuesAreEqual } from '../lib/version-history-diff';
import { ConfirmDialog } from './ConfirmDialog';
import { PILOT_COMPETENCY_FULL_NAMES } from '@orca360/shared';

export interface VersionHistoryDrawerProps {
  assessmentId: string | undefined;
  /** Required for pilot/instructor — drives session-level version queries. */
  sessionId?: string;
  assessmentType: AssessmentType;
  isOpen: boolean;
  onClose: () => void;
  onRestore: (snapshot: Record<string, unknown>, fromVersionNumber: number) => Promise<void>;
}

// ─── Constants ───────────────────────────────────────────────────────────────

// GAT-04: grade 3 Adequate meets the required standard, so it is neutral and
// never shares the warning family used for grades 1 and 2.
const SCORE_COLORS: Record<number, string> = {
  1: 'bg-red-100 text-red-700',
  2: 'bg-orange-100 text-orange-700',
  3: 'bg-slate-100 text-slate-700',
  4: 'bg-blue-100 text-blue-700',
  5: 'bg-green-100 text-green-700',
};

const FIELD_LABELS: Record<string, string> = {
  score: 'Grade',
  analysis: 'Analysis',
  goals: 'Goals / Action Plan',
  evidence: 'Evidence',
  part_a: 'Part A — Session Identification',
  part_b: 'Part B — Debriefing Framework',
  part_c: 'Part C — Supporting Material',
  part_d: 'Part D — Instructor Notes',
  status: 'Status',
  invocation_line: 'Invocation Line',
};

// Single source: shared framework module (long-form display names).
const COMPETENCY_LABELS: Record<string, string> = PILOT_COMPETENCY_FULL_NAMES;

const IEC_CRITERION_LABELS: Record<string, string> = {
  IEC1: 'Pilot competencies',
  IEC2: 'Management of the learning environment',
  IEC3: 'Instruction',
  IEC4: 'Interaction',
  IEC5: 'Assessment and evaluation',
};

const TEXT_DIFF_FIELDS = new Set([
  'analysis', 'goals',
  'part_a', 'part_b', 'part_c', 'part_d', 'invocation_line',
  'evidence',
]);

// ─── Helpers ─────────────────────────────────────────────────────────────────

function crewRoleLabel(role: string): string {
  switch (role) {
    case 'captain':         return 'Captain';
    case 'captain_2':       return 'Captain 2';
    case 'first_officer':   return 'First Officer';
    case 'first_officer_2': return 'First Officer 2';
    default: return role;
  }
}

function ScoreBadge({ score }: { score: number }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${SCORE_COLORS[score] ?? 'bg-gray-100 text-gray-600'}`}>
      {score}/5
    </span>
  );
}

function truncate(text: string, max = 200): string {
  return text && text.length > max ? text.slice(0, max) + '…' : text;
}

function toDiffText(val: unknown, field: string): string {
  if (val === undefined || val === null) return '—';
  if (typeof val === 'string') return val;
  if (field === 'evidence' && Array.isArray(val)) {
    return (val as Array<{ quote?: string }>)
      .map((item) => item.quote?.trim())
      .filter(Boolean)
      .join('\n');
  }
  if (typeof val === 'object') return JSON.stringify(val, null, 2);
  return String(val);
}

function getInlineDiff(oldText: string, newText: string) {
  let prefixLength = 0;
  const maxPrefix = Math.min(oldText.length, newText.length);
  while (prefixLength < maxPrefix && oldText[prefixLength] === newText[prefixLength]) prefixLength += 1;

  let oldSuffixLength = oldText.length - 1;
  let newSuffixLength = newText.length - 1;
  while (
    oldSuffixLength >= prefixLength &&
    newSuffixLength >= prefixLength &&
    oldText[oldSuffixLength] === newText[newSuffixLength]
  ) { oldSuffixLength -= 1; newSuffixLength -= 1; }

  return {
    prefix:     oldText.slice(0, prefixLength),
    oldChanged: oldText.slice(prefixLength, oldSuffixLength + 1),
    newChanged: newText.slice(prefixLength, newSuffixLength + 1),
    suffix:     oldText.slice(oldSuffixLength + 1),
  };
}

// ─── Diff types ───────────────────────────────────────────────────────────────

interface DiffEntry {
  field:  string;
  label:  string;
  oldVal: unknown;
  newVal: unknown;
}

interface SessionDiffEntry extends DiffEntry {
  pilotRole:  string;
  competency: string;
}

// ─── Per-assessment diff (instructor / debriefing_guide) ──────────────────────

function extractFields(
  snapshot: Record<string, unknown>,
  assessmentType: AssessmentType,
): Record<string, unknown> {
  if (assessmentType === 'pilot' || assessmentType === 'instructor') {
    return { score: snapshot.score, analysis: snapshot.analysis, goals: snapshot.goals, evidence: snapshot.evidence };
  }
  return {
    part_a: snapshot.part_a, part_b: snapshot.part_b,
    part_c: snapshot.part_c, part_d: snapshot.part_d,
    invocation_line: snapshot.invocation_line,
  };
}

function getDiffs(
  a: Record<string, unknown>,
  b: Record<string, unknown>,
  assessmentType: AssessmentType,
): DiffEntry[] {
  const fieldsA = extractFields(a, assessmentType);
  const fieldsB = extractFields(b, assessmentType);
  return Object.keys(fieldsA)
    .filter((field) => !valuesAreEqual(fieldsA[field], fieldsB[field]))
    .map((field) => ({ field, label: FIELD_LABELS[field] ?? field, oldVal: fieldsA[field], newVal: fieldsB[field] }));
}

// ─── Session-level pilot diff ─────────────────────────────────────────────────

/**
 * MNT-33, migration 293: an AI baseline that a later regeneration replaced. The
 * row is retained rather than deleted, so history grows and the seven year
 * retention rule is honoured. It keeps is_ai_baseline true deliberately, so that
 * a client which has not been updated still refuses to restore it.
 */
function isSupersededAiBaseline(version: AssessmentVersion): boolean {
  // Tested on superseded_at ALONE, not on is_ai_baseline as well. Only
  // finalize_ai_assessment_baseline ever sets the column, and only on a baseline
  // row, so the column is sufficient on its own. Requiring the flag too would
  // make this guard collapse in exactly the case it exists to cover: a future
  // change that clears is_ai_baseline when superseding.
  return version.superseded_at != null;
}

function isAiOriginalVersion(version: AssessmentVersion): boolean {
  return (
    version.is_ai_baseline
    && !isSupersededAiBaseline(version)
    && (version.version_number === AI_BASELINE_VERSION_NUMBER || version.session_id != null)
  );
}

/** Session-level composite diff (pilot roles × competencies or IEC criteria). */
function usesSessionLevelDiff(
  version: AssessmentVersion | undefined,
  assessmentType: AssessmentType,
): boolean {
  if (!version) return false;
  if (assessmentType === 'debriefing_guide') return false;
  if (isAiOriginalVersion(version)) return true;
  return version.session_id !== null && version.session_id !== undefined;
}

function versionDisplayName(version: AssessmentVersion): string {
  if (isAiOriginalVersion(version)) return 'AI Original';
  if (isSupersededAiBaseline(version)) return `AI Original (v${version.version_number})`;
  return `Version ${version.version_number}`;
}

// ─── Render helpers ───────────────────────────────────────────────────────────

function renderFieldValue(val: unknown, field: string): React.ReactNode {
  if (val === undefined || val === null) return <span className="text-gray-400 italic">—</span>;
  if (field === 'score' && typeof val === 'number') return <ScoreBadge score={val} />;
  if (typeof val === 'string') return <span className="text-sm text-gray-700 whitespace-pre-wrap">{truncate(val, 180)}</span>;
  if (field === 'evidence' && Array.isArray(val)) {
    const items = val as Array<{ quote?: string }>;
    return (
      <div>
        <span className="font-semibold text-sm">{items.length} item{items.length !== 1 ? 's' : ''}</span>
        {items.slice(0, 2).map((ev, i) => (
          <p key={i} className="text-xs text-gray-600 mt-1 truncate">&ldquo;{truncate(ev.quote ?? '', 80)}&rdquo;</p>
        ))}
        {items.length > 2 && <p className="text-xs text-gray-400 mt-0.5">+{items.length - 2} more</p>}
      </div>
    );
  }
  return <span className="text-sm text-gray-500 italic">Modified</span>;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function InlineTextDiff({ oldText, newText }: { oldText: string; newText: string }) {
  const diff = getInlineDiff(oldText, newText);
  return (
    <div className="grid grid-cols-2 divide-x divide-gray-200">
      <div className="p-3 bg-red-50 min-h-[48px]">
        <div className="text-xs text-red-400 mb-1 font-medium">Version A</div>
        <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">
          <span>{diff.prefix}</span>
          {diff.oldChanged && <span className="bg-red-200 rounded px-0.5">{diff.oldChanged}</span>}
          <span>{diff.suffix}</span>
        </p>
      </div>
      <div className="p-3 bg-green-50 min-h-[48px]">
        <div className="text-xs text-green-500 mb-1 font-medium">Version B</div>
        <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">
          <span>{diff.prefix}</span>
          {diff.newChanged && <span className="bg-green-200 rounded px-0.5">{diff.newChanged}</span>}
          <span>{diff.suffix}</span>
        </p>
      </div>
    </div>
  );
}

function DiffRow({ field, label, oldVal, newVal }: DiffEntry) {
  const showTextDiff = TEXT_DIFF_FIELDS.has(field);
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden mb-3">
      <div className="bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</div>
      {showTextDiff ? (
        <InlineTextDiff oldText={toDiffText(oldVal, field)} newText={toDiffText(newVal, field)} />
      ) : (
        <div className="grid grid-cols-2 divide-x divide-gray-200">
          <div className="p-3 bg-red-50 min-h-[48px]">
            <div className="text-xs text-red-400 mb-1 font-medium">Version A</div>
            {renderFieldValue(oldVal, field)}
          </div>
          <div className="p-3 bg-green-50 min-h-[48px]">
            <div className="text-xs text-green-500 mb-1 font-medium">Version B</div>
            {renderFieldValue(newVal, field)}
          </div>
        </div>
      )}
    </div>
  );
}

const COLLAPSE_THRESHOLD = 5;

/** Grouped diff for session-level pilot/instructor snapshots. */
function SessionDiffView({ diffs, assessmentType }: { diffs: SessionDiffEntry[]; assessmentType: AssessmentType }) {
  const groups = useMemo(() => {
    const map = new Map<string, SessionDiffEntry[]>();
    for (const d of diffs) {
      const key = `${d.pilotRole}__${d.competency}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(d);
    }
    return map;
  }, [diffs]);

  const allKeys = useMemo(() => Array.from(groups.keys()), [groups]);
  const defaultCollapsed = allKeys.length >= COLLAPSE_THRESHOLD;
  const initialised = useRef(false);
  const [expanded, setExpanded] = useState<Set<string>>(() =>
    defaultCollapsed ? new Set() : new Set(allKeys),
  );

  // Re-initialise when the diff changes (version selectors changed)
  useEffect(() => {
    if (!initialised.current) { initialised.current = true; return; }
    setExpanded(allKeys.length >= COLLAPSE_THRESHOLD ? new Set() : new Set(allKeys));
  }, [allKeys]);

  const allExpanded = expanded.size === allKeys.length;

  const toggleGroup = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const toggleAll = () =>
    setExpanded(allExpanded ? new Set() : new Set(allKeys));

  if (groups.size === 0) {
    return (
      <div className="text-center py-10">
        <p className="text-sm text-gray-500 font-medium">No differences</p>
        <p className="text-xs text-gray-400 mt-1">These versions are identical.</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-gray-500">
          {diffs.length} change{diffs.length !== 1 ? 's' : ''} across {groups.size} section{groups.size !== 1 ? 's' : ''}
        </p>
        <button
          onClick={toggleAll}
          className="text-xs text-navy hover:underline font-medium"
        >
          {allExpanded ? 'Collapse all' : 'Expand all'}
        </button>
      </div>

      {Array.from(groups.entries()).map(([key, entries]) => {
        const first = entries[0];
        if (!first) return null;
        const { pilotRole, competency } = first;
        const isInstructor = assessmentType === 'instructor';
        const isExpanded = expanded.has(key);

        return (
          <div key={key} className="mb-3 border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => toggleGroup(key)}
              className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
            >
              <div className="flex items-center gap-2 flex-wrap">
                {isInstructor ? (
                  <>
                    <span className="text-xs font-bold text-navy uppercase tracking-wide">{pilotRole}</span>
                    <span className="text-xs text-gray-400">— {IEC_CRITERION_LABELS[pilotRole] ?? pilotRole}</span>
                  </>
                ) : (
                  <>
                    <span className="text-xs font-bold text-navy uppercase tracking-wide">{crewRoleLabel(pilotRole)}</span>
                    <span className="text-xs text-gray-400">— {COMPETENCY_LABELS[competency] ?? competency}</span>
                  </>
                )}
                <span className="text-xs text-gray-400">({entries.length} field{entries.length !== 1 ? 's' : ''})</span>
              </div>
              {isExpanded
                ? <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                : <ChevronRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />}
            </button>

            {isExpanded && (
              <div className="p-3">
                {entries.map((d) => (
                  <DiffRow key={d.field} field={d.field} label={d.label} oldVal={d.oldVal} newVal={d.newVal} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

function VersionRow({ version, authorLabel, isRestoring, isLatest, onRestore }: {
  version: AssessmentVersion;
  authorLabel: string;
  isRestoring: boolean;
  isLatest: boolean;
  onRestore: () => void;
}) {
  // MNT-33: supersession is read explicitly rather than being left to
  // is_ai_baseline alone. Migration 293 keeps the flag true on a superseded
  // baseline precisely so this gate stays closed, and naming the condition here
  // means restore still refuses if that ever changes.
  const superseded = isSupersededAiBaseline(version);
  const canRestore = !version.is_ai_baseline && !superseded && !isLatest;
  return (
    <div className={`border rounded-lg p-4 ${version.is_ai_baseline ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-white'} ${superseded ? 'opacity-75' : ''}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-navy text-sm">{versionDisplayName(version)}</span>
            {isLatest && !version.is_ai_baseline && (
              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Current</span>
            )}
            {version.is_ai_baseline && (
              <span className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                <Bot className="w-3 h-3" /> AI Generated
              </span>
            )}
            {superseded && (
              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                Superseded
              </span>
            )}
            {version.session_id && !version.is_ai_baseline && (
              <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Full session</span>
            )}
          </div>
          <div className="flex items-center gap-1.5 mt-1 text-xs text-gray-500">
            <User className="w-3 h-3 shrink-0" />
            <span className="truncate">{authorLabel}</span>
            <span className="text-gray-300">•</span>
            <span className="shrink-0">{format(new Date(version.created_at), 'PP p')}</span>
          </div>
          {version.change_summary && (
            <p className="mt-1.5 text-xs text-gray-600 italic">{version.change_summary}</p>
          )}
        </div>
        {canRestore && (
          <button
            onClick={onRestore}
            disabled={isRestoring}
            className="shrink-0 flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-navy border border-gray-200 hover:border-navy px-2.5 py-1.5 rounded-md transition-colors disabled:opacity-50"
          >
            <RotateCcw className="w-3 h-3" />
            {isRestoring ? 'Restoring…' : 'Restore'}
          </button>
        )}
      </div>
    </div>
  );
}

function VersionList({ versions, getAuthorLabel, restoringVersion, onRestore }: {
  versions: AssessmentVersion[];
  getAuthorLabel: (v: AssessmentVersion) => string;
  restoringVersion: number | null;
  onRestore: (v: AssessmentVersion) => void;
}) {
  const latestVersionNumber = versions[0]?.version_number;
  return (
    <div className="p-4 space-y-2">
      {versions.map((v) => (
        <VersionRow
          key={v.id}
          version={v}
          authorLabel={getAuthorLabel(v)}
          isRestoring={restoringVersion === v.version_number}
          isLatest={v.version_number === latestVersionNumber}
          onRestore={() => onRestore(v)}
        />
      ))}
    </div>
  );
}

function DiffPane({ versions, assessmentType, compareLeft, compareRight, onChangeLeft, onChangeRight, getAuthorLabel }: {
  versions: AssessmentVersion[];
  assessmentType: AssessmentType;
  compareLeft: number | null;
  compareRight: number | null;
  onChangeLeft: (n: number) => void;
  onChangeRight: (n: number) => void;
  getAuthorLabel: (v: AssessmentVersion) => string;
}) {
  const versionA = useMemo(() => versions.find((v) => v.version_number === compareLeft), [versions, compareLeft]);
  const versionB = useMemo(() => versions.find((v) => v.version_number === compareRight), [versions, compareRight]);

  const sessionLevel = usesSessionLevelDiff(versionA, assessmentType);

  const sessionDiffs = useMemo(
    () => sessionLevel && versionA && versionB && compareLeft !== compareRight
      ? getSessionVersionDiffs(versionA.snapshot, versionB.snapshot, assessmentType as 'pilot' | 'instructor')
      : [],
    [sessionLevel, versionA, versionB, compareLeft, compareRight, assessmentType],
  );

  const flatDiffs = useMemo(
    () => !sessionLevel && versionA && versionB && compareLeft !== compareRight
      ? getDiffs(versionA.snapshot, versionB.snapshot, assessmentType)
      : [],
    [sessionLevel, versionA, versionB, compareLeft, compareRight, assessmentType],
  );

  const versionLabel = (v: AssessmentVersion) => {
    if (isAiOriginalVersion(v)) return 'AI Original';
    return `v${v.version_number} — ${v.is_ai_baseline ? 'AI Generated' : getAuthorLabel(v)}`;
  };

  return (
    <div>
      <div className="sticky top-0 z-10 bg-white border-b border-gray-100 px-4 py-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Version A</label>
            <select
              value={compareLeft ?? ''}
              onChange={(e) => onChangeLeft(Number(e.target.value))}
              className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:border-navy"
            >
              {versions.map((v) => (
                <option key={v.version_number} value={v.version_number}>{versionLabel(v)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Version B</label>
            <select
              value={compareRight ?? ''}
              onChange={(e) => onChangeRight(Number(e.target.value))}
              className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:border-navy"
            >
              {versions.map((v) => (
                <option key={v.version_number} value={v.version_number}>{versionLabel(v)}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="p-4">
      {!versionA || !versionB ? (
        <p className="text-sm text-gray-400 text-center py-10">Select two versions to compare</p>
      ) : compareLeft === compareRight ? (
        <p className="text-sm text-gray-400 text-center py-10">Select two different versions</p>
      ) : sessionLevel ? (
        <SessionDiffView diffs={sessionDiffs} assessmentType={assessmentType} />
      ) : flatDiffs.length === 0 ? (
        <div className="text-center py-10">
          <p className="text-sm text-gray-500 font-medium">No differences</p>
          <p className="text-xs text-gray-400 mt-1">These versions are identical.</p>
        </div>
      ) : (
        <>
          <p className="text-xs text-gray-500 mb-3">{flatDiffs.length} field{flatDiffs.length !== 1 ? 's' : ''} changed</p>
          {flatDiffs.map((d) => (
            <DiffRow key={d.field} field={d.field} label={d.label} oldVal={d.oldVal} newVal={d.newVal} />
          ))}
        </>
      )}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function VersionHistoryDrawer({
  assessmentId, sessionId, assessmentType, isOpen, onClose, onRestore,
}: VersionHistoryDrawerProps) {
  const [versions, setVersions] = useState<AssessmentVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [userNames, setUserNames] = useState<Record<string, string>>({});
  const [mode, setMode] = useState<'list' | 'diff'>('list');
  const [compareLeft, setCompareLeft] = useState<number | null>(null);
  const [compareRight, setCompareRight] = useState<number | null>(null);
  const [restoringVersion, setRestoringVersion] = useState<number | null>(null);
  const [pendingRestoreVersion, setPendingRestoreVersion] = useState<AssessmentVersion | null>(null);

  const loadVersions = useCallback(async () => {
    const usesSession =
      (assessmentType === 'pilot' || assessmentType === 'instructor') && !!sessionId;
    if (!usesSession && !assessmentId) return;

    setLoading(true);
    try {
      const data = usesSession
        ? await getSessionVersionsWithAiBaseline(sessionId!, assessmentType as 'pilot' | 'instructor')
        : await getVersions(assessmentId, assessmentType, undefined);
      setVersions(data);

      const userIds = [...new Set(data.map((v) => v.created_by).filter(Boolean))] as string[];
      if (userIds.length > 0) {
        const { data: users } = await supabase.from('users').select('id, full_name').in('id', userIds);
        if (users) {
          const nameMap: Record<string, string> = {};
          (users as { id: string; full_name: string }[]).forEach((u) => { nameMap[u.id] = u.full_name; });
          setUserNames(nameMap);
        }
      }

      const aiVersion = data.find((v) => isAiOriginalVersion(v));
      const latestManual = data.find((v) => !v.is_ai_baseline);

      if (aiVersion && latestManual) {
        setCompareLeft(aiVersion.version_number);
        setCompareRight(latestManual.version_number);
      } else if (data.length >= 2) {
        setCompareLeft(data[data.length - 1]!.version_number);
        setCompareRight(data[0]!.version_number);
      } else {
        setCompareLeft(null);
        setCompareRight(data[0]?.version_number ?? null);
      }
    } finally {
      setLoading(false);
    }
  }, [assessmentId, sessionId, assessmentType]);

  useEffect(() => {
    if (isOpen) { setMode('list'); loadVersions(); }
  }, [isOpen, loadVersions]);

  const handleRestoreRequest = useCallback((version: AssessmentVersion) => {
    setPendingRestoreVersion(version);
  }, []);

  const handleConfirmRestore = useCallback(async () => {
    if (!pendingRestoreVersion) return;
    setRestoringVersion(pendingRestoreVersion.version_number);
    try {
      await onRestore(pendingRestoreVersion.snapshot, pendingRestoreVersion.version_number);
      await loadVersions();
      setPendingRestoreVersion(null);
    } finally {
      setRestoringVersion(null);
    }
  }, [pendingRestoreVersion, onRestore, loadVersions]);

  const getAuthorLabel = useCallback(
    (v: AssessmentVersion) => {
      if (v.is_ai_baseline) return 'AI Generated';
      if (v.created_by) { const name = userNames[v.created_by]; if (name) return name; }
      return 'Instructor';
    },
    [userNames],
  );

  const restoreDescription = useMemo(() => {
    if (!pendingRestoreVersion) return '';
    const versionName = versionDisplayName(pendingRestoreVersion);
    const base = `Restore to ${versionName}? The current state will be saved as a new version first.`;
    if (!pendingRestoreVersion.session_id) return base;
    if (assessmentType === 'instructor') {
      return `${base} All IEC criteria will be restored together.`;
    }
    return `${base} All crew roles and competencies will be restored together.`;
  }, [pendingRestoreVersion, assessmentType]);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />

      <div className="fixed top-0 right-0 bottom-0 w-[480px] bg-white z-50 shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-navy text-white shrink-0">
          <div className="flex items-center gap-2">
            <History className="w-5 h-5" />
            <h2 className="font-semibold text-lg text-white">Version History</h2>
            {versions.length > 0 && (
              <span className="text-xs bg-white/20 rounded-full px-2 py-0.5">{versions.length}</span>
            )}
          </div>
          <button onClick={onClose} className="hover:text-gray-300 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        {versions.length >= 2 && (
          <div className="flex border-b border-gray-200 shrink-0">
            <button
              onClick={() => setMode('list')}
              className={`flex-1 py-2.5 text-sm font-medium transition-colors ${mode === 'list' ? 'text-navy border-b-2 border-navy bg-blue-50' : 'text-gray-500 hover:text-gray-700'}`}
            >
              All Versions
            </button>
            <button
              onClick={() => setMode('diff')}
              className={`flex-1 py-2.5 text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${mode === 'diff' ? 'text-navy border-b-2 border-navy bg-blue-50' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <ArrowLeftRight className="w-3.5 h-3.5" />
              Compare
            </button>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy" />
            </div>
          ) : versions.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-56 text-center px-8">
              <Clock className="w-10 h-10 text-gray-300 mb-3" />
              <p className="text-gray-500 font-medium">No version history yet</p>
              <p className="text-gray-400 text-sm mt-1">
                Save your first edit to start tracking changes across all competencies.
              </p>
            </div>
          ) : mode === 'list' ? (
            <VersionList
              versions={versions}
              getAuthorLabel={getAuthorLabel}
              restoringVersion={restoringVersion}
              onRestore={handleRestoreRequest}
            />
          ) : (
            <DiffPane
              versions={versions}
              assessmentType={assessmentType}
              compareLeft={compareLeft}
              compareRight={compareRight}
              onChangeLeft={setCompareLeft}
              onChangeRight={setCompareRight}
              getAuthorLabel={getAuthorLabel}
            />
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={pendingRestoreVersion !== null}
        title="Restore Version"
        description={restoreDescription}
        confirmLabel="Restore version"
        cancelLabel="Keep current"
        isConfirming={pendingRestoreVersion !== null && restoringVersion === pendingRestoreVersion.version_number}
        onConfirm={handleConfirmRestore}
        onCancel={() => setPendingRestoreVersion(null)}
      />
    </>
  );
}
